import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { ReactNode } from 'react';
import { ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';
import { generateNanoId } from 'src/features/flows/lib/general';
import { cn } from 'src/shared/lib/utils';

type CheckboxProps = ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
  indeterminate?: boolean;
};

const Checkbox = forwardRef<ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, ...props }, ref) => (
    <CheckboxPrimitive.Root
      ref={ref}
      disabled={props.disabled}
      className={cn(
        `peer h-4 w-4 shrink-0 rounded-sm border border-neutral-400 hover:border-primary group-hover:border-primary  
        focus-visible:outline-none disabled:cursor-not-allowed disabled:data-[state=checked]:hover:border-primary
        disabled:pointer-events-none disabled:opacity-50 disabled:group-hover:border-neutral-400
        data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-typography-white`,
        props.checked === true && 'bg-primary border-primary text-typography-white',
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
        {props.checked === 'indeterminate' ? (
          <Minus className="h-[15px] w-3 group-hover:text-primary" />
        ) : (
          <Check size={16} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

type CheckboxItemProps = {
  children: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: CheckboxPrimitive.CheckedState) => void;
  disabled?: boolean;
};

function CheckboxItem({ children, checked, onCheckedChange, disabled }: CheckboxItemProps) {
  const id = generateNanoId();

  return (
    <div className="flex gap-2 items-center mt-1 group">
      <Checkbox id={id} className="peer" disabled={disabled} checked={checked} onCheckedChange={onCheckedChange} />
      <label
        className="leading-none text-sm select-none group-hover:text-typography-black transition-all text-typography-grey cursor-pointer peer-data-[state=checked]:text-typography-black"
        htmlFor={id}
      >
        {children}
      </label>
    </div>
  );
}

export { Checkbox, CheckboxItem };
